import {
  minimalDataCollection,
  noopErrorReporter,
  sentryHint,
  type ErrorReporter,
} from '@estimating/error-reporting';
import * as Sentry from '@sentry/react';
import type { AnyRouter } from '@tanstack/react-router';
import type { RuntimeConfig } from './runtime-config';

declare const __APP_RELEASE__: string;

/** This build's release, such as `estimating-web@0.1.0+<commit>`; set by vite.config.ts. */
const APP_RELEASE = __APP_RELEASE__;

type SentrySettings = NonNullable<RuntimeConfig['sentry']>;

export function sentryBrowserOptions(
  config: RuntimeConfig,
  sentry: SentrySettings,
  release: string,
  router: AnyRouter,
): Sentry.BrowserOptions {
  return {
    dsn: sentry.dsn,
    environment: config.environment,
    release,
    dataCollection: minimalDataCollection(),
    initialScope: { tags: { brand: config.brand } },
    // Session replay is deliberately absent: it would record estimates on screen.
    ...(sentry.tracesSampleRate === undefined
      ? {}
      : {
          tracesSampleRate: sentry.tracesSampleRate,
          // Names page loads and navigations by route template, never by the IDs in the URL.
          integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
          // Trace headers go to our API only, which allows them through CORS.
          tracePropagationTargets: [`${new URL(config.apiBaseUrl).origin}/`],
        }),
  };
}

export const sentryErrorReporter: ErrorReporter = {
  captureException(error, context) {
    const hint = sentryHint(context);
    if (context?.componentStack === undefined) {
      Sentry.captureException(error, hint);
    } else {
      // Attaches the component stack as the error's cause, so Sentry shows where it rendered.
      Sentry.captureReactException(error, { componentStack: context.componentStack }, hint);
    }
  },
  flush: (timeoutMs) => Sentry.flush(timeoutMs),
};

/** Starts Sentry if the stamp's runtime config has a DSN, and returns the reporter to inject. */
export function initSentry(config: RuntimeConfig, router: AnyRouter): ErrorReporter {
  if (config.sentry === undefined) {
    return noopErrorReporter;
  }
  Sentry.init(sentryBrowserOptions(config, config.sentry, APP_RELEASE, router));
  return sentryErrorReporter;
}
